import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Row, Col, Form, FloatingLabel } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import {
  CountrySelect,
  StateSelect,
  GetCountries,
  GetState,
} from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PhoneVerification from "../../components/phoneVerificationForm";
import { useAuth } from "../../contexts/authContext";
import { useNotification } from "../../components/notification";

// Define validation schema using yup
const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  address1: yup.string().required("Address line 1 is required"),
  address2: yup.string().optional(),
  city: yup.string().required("City is required"),
  state: yup.string().required("State is required"),
  postalCode: yup
    .string()
    .required("Postal code is required")
    .matches(/^\d{6}$/, "Postal code must be 6 digits"),
  country: yup.string().required("Country is required"),
  phone: yup
    .string()
    .required("Phone is required")
    .matches(/^\d{10}$/, "Phone must be 10 digits"),
  type: yup.string().required("Address type is required"),
  // isDefault: yup.boolean().default(false),
});

const AddressForm = forwardRef(({ onSubmit, existingAddress }, ref) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      phone: "",
      type: "",
      phoneVerified: false,
    },
  });

  const { showNotification } = useNotification();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [isPhoneVerified, setPhoneVerified] = useState(false);
  const { user } = useAuth();
  const [phone, setPhone] = useState(existingAddress?.phone || "");

  useEffect(() => {
    GetCountries().then((result) => {
      setCountries(result);
    });
  }, []);

  useEffect(() => {
    if (existingAddress) {
      reset(existingAddress);
      setPhone(existingAddress.phone);
      const country = countries.find((c) => c.name === existingAddress.country);
      if (country) {
        setSelectedCountry(country);
        GetState(parseInt(country.id)).then((states) => {
          const state = states.find((s) => s.name === existingAddress.state);
          setSelectedState(state);
        });
      }
    }
  }, [existingAddress, reset, countries]);

  // Expose the handleSubmit function to the parent component
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      let isPhoneValid = phone && (
        (phone === existingAddress?.phone && existingAddress?.phoneVerified) ||
        (user?.phone === phone && user?.phoneVerified) ||
        isPhoneVerified
      );
      
      if (!isPhoneValid) {
        showNotification("danger", "Phone number must be verified.", 5000);
        return;
      }      
      setValue("phoneVerified", isPhoneValid);
      handleSubmit(onSubmit)();
    },
    isFormValid: () => {
      let isPhoneValid = phone && (
        (phone === existingAddress?.phone && existingAddress?.phoneVerified) ||
        (user?.phone === phone && user?.phoneVerified) ||
        isPhoneVerified
      );
      setValue("phoneVerified", isPhoneValid);
      return isPhoneValid;
    },
  }));

  useEffect(() => {
    const inputCountryEl = document.querySelector("input[name='country']");
    if (inputCountryEl) {
      inputCountryEl.setAttribute("autocomplete", "off");
    }

    const inputStateEl = document.querySelector("input[name='state']");
    if (inputStateEl) {
      inputStateEl.setAttribute("autocomplete", "off");
    }
  }, [selectedCountry]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col md={6}>
          <FloatingLabel
            controlId="txtFirstName"
            label="First Name"
            className="mb-3"
          >
            <Form.Control
              type="text"
              placeholder="First Name"
              {...register("firstName")}
              isInvalid={!!errors.firstName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstName?.message}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>
        <Col md={6}>
          <FloatingLabel
            controlId="txtLastName"
            label="Last Name"
            className="mb-3"
          >
            <Form.Control
              type="text"
              placeholder="Last Name"
              {...register("lastName")}
              isInvalid={!!errors.lastName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastName?.message}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>
        <Col md={6}>
          <FloatingLabel
            controlId="txtAddress1"
            label="Address Line 1"
            className="mb-3"
          >
            <Form.Control
              type="text"
              placeholder="Address Line 1"
              {...register("address1")}
              isInvalid={!!errors.address1}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address1?.message}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>

        <Col md={6}>
          <FloatingLabel
            controlId="txtAddress2"
            label="Address Line 2"
            className="mb-3"
          >
            <Form.Control
              type="text"
              placeholder="Address Line 2"
              {...register("address2")}
              isInvalid={!!errors.address2}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address2?.message}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>
        <Col md={6}>
          <FloatingLabel controlId="txtCity" label="City" className="mb-3">
            <Form.Control
              type="text"
              placeholder="City"
              {...register("city")}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback type="invalid">
              {errors.city?.message}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>
        <Col md={6}>
          <Controller
            name="country"
            control={control}
            defaultValue={null}
            render={({ field }) => (
              <CountrySelect
                containerClassName="form-select mb-3"
                inputClassName="form-control px-0"
                {...field}
                onChange={(selectedCountry) => {
                  field.onChange(selectedCountry.name);
                  setSelectedCountry(selectedCountry);
                }}
                placeHolder="Select Country"
                defaultValue={selectedCountry}
              />
            )}
          />
          {errors.country && (
            <p className="text-danger">{errors.country.message}</p>
          )}
        </Col>

        <Col md={6}>
          <Controller
            name="state"
            control={control}
            defaultValue={null}
            render={({ field }) => (
              <StateSelect
                countryid={selectedCountry?.id}
                containerClassName="form-select mb-3"
                inputClassName="form-control px-0"
                {...field}
                onChange={(selectedState) => {
                  field.onChange(selectedState.name);
                  setSelectedState(selectedState);
                }}
                placeHolder="Select State"
                defaultValue={selectedState}
              />
            )}
          />
          {errors.state && (
            <p className="text-danger">{errors.state.message}</p>
          )}
        </Col>
        <Col md={6}>
          <FloatingLabel
            controlId="txtPostalCode"
            label="Postal Code"
            className="mb-3"
          >
            <Form.Control
              type="text"
              placeholder="Postal Code"
              {...register("postalCode")}
              isInvalid={!!errors.postalCode}
            />
            <Form.Control.Feedback type="invalid">
              {errors.postalCode?.message}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>

        <Col md={6}>
          <PhoneVerification
            value={phone}
            onChange={(val) => {
              if (val) {
                setPhone(val);
                setValue("phone", val);
              }
            }}
            onVerified={setPhoneVerified}
            disabled={isSubmitting}
            existingPhone={existingAddress?.phone || ""}
            isPhoneVerified={existingAddress?.phoneVerified}
            isExistingVerified={user?.phoneVerified }
            isAddress={true}
            existingAddressPhone={existingAddress?.phone}
            isAddressPhoneVerified={existingAddress?.phoneVerified}
          />
        </Col>
        <Col md={6}>
          <FloatingLabel
            controlId="ddAddressType"
            label="Address Type"
            className="mb-3"
          >
            <Form.Select {...register("type")} isInvalid={!!errors.type}>
              <option value="">Choose...</option>
              <option value="shipping">Shipping</option>
              <option value="billing">Billing</option>
              <option value="both">Both</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.type?.message}
            </Form.Control.Feedback>
          </FloatingLabel>
        </Col>
      </Row>
    </Form>
  );
});

export default AddressForm;
